import { api } from './api';

/**
 * Só é recusa de negócio quando o Funifier devolve a mensagem da
 * `BusinessException` do trigger, prefixada pela classe:
 *   "FunifierTrigger$BusinessException: Descreva o motivo..."
 * O prefixo não interessa a quem lê; o texto depois dos dois pontos sim.
 *
 * Qualquer outro `message` — permissão negada, NullPointerException de um
 * `before_*` quebrado, timeout de gateway — NÃO é recusa de negócio: é falha
 * inesperada, e quem chama esta função deve tratá-la como tal (ex.: toast
 * genérico), nunca exibir o texto bruto como se fosse o motivo da recusa.
 */
const PREFIXO_BUSINESS_EXCEPTION = /^[\w$.]*BusinessException:\s*/;

export function mensagemDaRecusa(erro: unknown): string | undefined {
  const bruta = (erro as any)?.response?.data?.message;

  if (typeof bruta !== 'string' || !PREFIXO_BUSINESS_EXCEPTION.test(bruta)) {
    return undefined;
  }

  const semPrefixo = bruta.replace(PREFIXO_BUSINESS_EXCEPTION, '').trim();

  return semPrefixo.length ? semPrefixo : undefined;
}

/**
 * POST, e não PUT. O evento do trigger depende do método HTTP: POST dispara
 * `before_create`, que é onde a validação vive; PUT dispararia `before_update`
 * e a denúncia entraria sem passar por validação alguma.
 *
 * O `interactions__c` do projeto usa PUT, mas aquela coleção não tem trigger —
 * copiar o padrão de lá aqui seria copiar o que não importa.
 */
export async function reportRecognition(idRec: string, texto: string) {
  const { data } = await api.post('database/den__c', { id_rec: idRec, texto });
  return data;
}

export type DecisaoDaSinalizacao = 'approved' | 'reproved';

export interface FeedbackPendente {
  _id: string;
  decisao: DecisaoDaSinalizacao;
  parecer: string;
}

/**
 * Só quem sinalizou recebe a resposta da equipe — quem escreveu o
 * reconhecimento nunca é avisado, nem quando a denúncia é procedente.
 * Quem filtra é o agregado, comparando `quem_enviou` com o jogador do
 * contexto: o cliente não diz quem é, senão qualquer jogador pediria a
 * resposta destinada a outra pessoa.
 *
 * Falha em silêncio de propósito: isto roda no carregamento do app, e um
 * erro aqui não pode impedir a pessoa de usar o resto da tela.
 */
export async function buscarFeedbackPendente(): Promise<FeedbackPendente[]> {
  try {
    const { data } = await api.post('find/feedback_sinalizacao', {});
    if (!Array.isArray(data)) return [];

    /* Parecer vazio geraria uma modal sem conteúdo que volta a cada
       carregamento e não tem como ser dispensada. O agregado já filtra;
       este é o cinto de segurança do lado do cliente. */
    return data.filter((item: FeedbackPendente) => typeof item?.parecer === 'string' && item.parecer.trim().length > 0);
  } catch {
    return [];
  }
}

/**
 * Deixa a exceção subir: quem chama só fecha a modal depois que gravou,
 * senão a pessoa perde o parecer sem ter confirmado nada.
 * O `gestorId` é carimbado pelo servidor e não vai daqui.
 */
export async function confirmarLeituraFeedback(denunciaId: string): Promise<void> {
  await api.post('database/read_notifications__c', {
    type: 'feedback_sinalizacao',
    denunciaId,
  });
}
